/**
 * Persistência da preferência de tema (Bug 79631, dentro do PBI 84883).
 *
 * O tema tem DOIS donos, e a distinção importa:
 *
 * - `localStorage` — preferência do DISPOSITIVO. Sobrevive ao logoff, porque
 *   não depende de sessão, e é o que permite pintar a tela certa já no
 *   primeiro frame (sem piscar claro antes de virar escuro).
 * - `user_settings.theme` no banco — preferência da CONTA. Segue o usuário
 *   entre máquinas e, quando existe, prevalece ao autenticar.
 *
 * A ordem de resolução na inicialização é: localStorage → preferência do
 * sistema operacional → default do provider. O banco entra depois, quando a
 * sessão resolve, e sobrescreve os dois.
 *
 * ATENÇÃO: a chave abaixo está duplicada no script inline de `index.html`, que
 * roda antes do React montar para eliminar o flash. Se mudar aqui, mude lá.
 */

export type Theme = 'dark' | 'light';

export const THEME_STORAGE_KEY = 'prodgy_theme';

const isTheme = (value: unknown): value is Theme =>
  value === 'dark' || value === 'light';

/** Preferência salva neste dispositivo, ou `null` se nunca houve escolha. */
export const readStoredTheme = (): Theme | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : null;
  } catch {
    // localStorage pode lançar em modo privado ou com cookies bloqueados;
    // sem preferência salva o app cai no default, que é comportamento válido.
    return null;
  }
};

export const writeStoredTheme = (theme: Theme): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Falha em persistir não pode derrubar a troca de tema em si.
  }
};

/** Tema do sistema operacional, usado só quando não há escolha registrada. */
export const readSystemTheme = (): Theme | null => {
  if (typeof window === 'undefined' || !window.matchMedia) return null;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : null;
};

/** Tema a aplicar no primeiro render, na ordem de precedência documentada. */
export const resolveInitialTheme = (fallback: Theme): Theme =>
  readStoredTheme() ?? readSystemTheme() ?? fallback;
